import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { uploadDrawingFile } from "../utils/firebase";
import api from "../utils/api";
import toast from "react-hot-toast";
import {
  PageHeader,
  Card,
  Button,
  Field,
  Input,
  Select,
  Sheet,
  Chip,
  ChipRow,
  SkeletonList,
  EmptyState,
  formatBytes,
  useBodyScrollLock,
  useEscape,
} from "../components/ui";
import { UploadIcon, DownloadIcon, EyeIcon } from "../components/icons";

const TYPE_STYLE = {
  pdf: "border-red-200 bg-red-50 text-red-700",
  dwg: "border-blue-200 bg-blue-50 text-blue-700",
  dxf: "border-cyan-200 bg-cyan-50 text-cyan-700",
};
const TYPE_ICON = { pdf: "📄", dwg: "📐", dxf: "📏" };
const ALLOWED = ["dwg", "dxf", "pdf"];
const MAX_BYTES = 100 * 1024 * 1024;

const extOf = (name = "") => name.split(".").pop().toLowerCase();

/* ─────────────────────────────────────────────────────────────
   Upload sheet
───────────────────────────────────────────────────────────── */

function UploadSheet({ sites, adminId, existingCodes, siteId, onSuccess, onClose }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    title: "",
    drawingCode: "",
    siteId: siteId || sites[0]?._id || "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = (f) => {
    if (!f) return; // user cancelled the picker
    const ext = extOf(f.name);
    if (!ALLOWED.includes(ext)) return toast.error("Only DWG, DXF or PDF files are allowed");
    if (f.size > MAX_BYTES) return toast.error("File is larger than 100 MB");
    setFile(f);
    if (!form.title) set("title", f.name.replace(/\.[^.]+$/, ""));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Select a file first");
    if (!form.title.trim()) return toast.error("Give the drawing a title");
    if (!form.siteId) return toast.error("Select a site");

    setUploading(true);
    setProgress(0);
    try {
      const { firebasePath, firebaseURL } = await uploadDrawingFile(
        file,
        adminId,
        form.siteId,
        setProgress
      );
      await api.post(`/drawings/site/${form.siteId}`, {
        title: form.title,
        fileType: extOf(file.name),
        fileName: file.name,
        fileSize: file.size,
        firebasePath,
        firebaseURL,
        drawingCode: form.drawingCode || undefined,
      });
      toast.success("Drawing uploaded ✅");
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Sheet
      title="Upload drawing"
      subtitle="DWG, DXF or PDF · up to 100 MB"
      onClose={uploading ? () => toast("Upload in progress…") : onClose}
      footer={
        <div className="flex gap-3">
          <Button
            variant="secondary"
            size="lg"
            block
            type="button"
            onClick={onClose}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="drawing-form"
            size="lg"
            block
            loading={uploading}
            disabled={!file}
          >
            {uploading ? `${progress}%` : "Upload"}
          </Button>
        </div>
      }
    >
      <form id="drawing-form" onSubmit={handleSubmit} className="space-y-5">
        {/* Drop / pick zone — a real button, so it is keyboard reachable */}
        <button
          type="button"
          onClick={() => !uploading && fileRef.current?.click()}
          disabled={uploading}
          className={`flex w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed px-4 py-8 transition-colors ${
            file
              ? "border-brand-500 bg-brand-50"
              : "border-slate-300 bg-slate-50 hover:border-brand-400 hover:bg-brand-50/50"
          }`}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".dwg,.dxf,.pdf"
            className="sr-only"
            onChange={(e) => handleFile(e.target.files?.[0])}
            disabled={uploading}
          />
          {file ? (
            <>
              <span className="text-3xl" aria-hidden="true">
                {TYPE_ICON[extOf(file.name)] || "📁"}
              </span>
              <span className="mt-2 max-w-full break-anywhere text-sm font-semibold text-slate-900">
                {file.name}
              </span>
              <span className="mt-0.5 text-xs text-slate-500">
                {formatBytes(file.size)} · tap to change
              </span>
            </>
          ) : (
            <>
              <UploadIcon className="h-7 w-7 text-slate-400" />
              <span className="mt-2 text-sm font-semibold text-slate-700">Choose a file</span>
              <span className="mt-0.5 text-xs text-slate-500">DWG, DXF or PDF</span>
            </>
          )}
        </button>

        {uploading && (
          <div>
            <div className="mb-1.5 flex justify-between text-xs font-medium text-slate-600">
              <span>Uploading…</span>
              <span className="tabular">{progress}%</span>
            </div>
            <div
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              className="h-2 w-full overflow-hidden rounded-full bg-slate-200"
            >
              <div
                className="h-full rounded-full bg-brand-600 transition-[width] duration-200"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {sites.length > 1 && (
          <Field label="Site" required htmlFor="dw-site">
            <Select id="dw-site" value={form.siteId} onChange={(e) => set("siteId", e.target.value)}>
              <option value="">Select site</option>
              {sites.map((s) => (
                <option key={s._id} value={s._id}>{s.name}</option>
              ))}
            </Select>
          </Field>
        )}

        <Field label="Drawing title" required htmlFor="dw-title">
          <Input
            id="dw-title"
            type="text"
            required
            placeholder="e.g. Plot boundary — north block"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
          />
        </Field>

        {existingCodes.length > 0 && (
          <Field
            label="Revision"
            htmlFor="dw-code"
            hint="Pick an existing code to upload this as a new version of that drawing."
          >
            <Select
              id="dw-code"
              value={form.drawingCode}
              onChange={(e) => set("drawingCode", e.target.value)}
            >
              <option value="">New drawing (auto code)</option>
              {existingCodes.map((c) => (
                <option key={c} value={c}>{c} — new version</option>
              ))}
            </Select>
          </Field>
        )}
      </form>
    </Sheet>
  );
}

/* ─────────────────────────────────────────────────────────────
   Full-screen preview
───────────────────────────────────────────────────────────── */

function PreviewModal({ drawing, onClose }) {
  useBodyScrollLock(true);
  useEscape(onClose);
  const isPdf = drawing.fileType === "pdf";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Preview: ${drawing.title}`}
      className="fixed inset-0 z-50 flex h-dvh flex-col bg-slate-900"
    >
      {/* Opaque white bar. Previously this header sat directly on the dark
          backdrop with slate-900 text, which made the title unreadable. */}
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3 pt-safe">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">{drawing.title}</p>
          <p className="truncate font-mono text-xs text-slate-500">
            {drawing.drawingCode} · v{drawing.version}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <a
            href={drawing.firebaseURL}
            target="_blank"
            rel="noopener noreferrer"
            download={drawing.fileName}
            className="tap inline-flex items-center gap-1.5 rounded-xl px-3 text-sm font-semibold text-brand-700 transition-colors hover:bg-brand-50"
          >
            <DownloadIcon className="h-4 w-4" />
            <span className="hidden xs:inline">Save</span>
          </a>
          <button
            onClick={onClose}
            aria-label="Close preview"
            className="tap flex items-center justify-center rounded-xl text-2xl leading-none text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
          >
            ×
          </button>
        </div>
      </div>

      <div className="relative flex-1 overflow-hidden bg-slate-800">
        <iframe
          src={
            isPdf
              ? drawing.firebaseURL
              : `https://sharecad.org/cadframe/load?url=${encodeURIComponent(drawing.firebaseURL)}`
          }
          title={isPdf ? "PDF preview" : "CAD preview"}
          className="h-full w-full border-0 bg-white"
        />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Page
───────────────────────────────────────────────────────────── */

export default function Drawings() {
  const { user } = useAuth();
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("");
  const [drawings, setDrawings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [preview, setPreview] = useState(null);
  const [filterType, setFilterType] = useState("");

  const fetchDrawings = useCallback(async () => {
    if (!selectedSite) return;
    try {
      const { data } = await api.get(`/drawings/site/${selectedSite}`);
      setDrawings(data.data || []);
    } catch {
      setDrawings([]);
    }
  }, [selectedSite]);

  useEffect(() => {
    api
      .get("/sites/crew")
      .then(({ data }) => {
        setSites(data.data || []);
        if (data.data?.length) setSelectedSite(data.data[0]._id);
      })
      .catch(() => setSites([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchDrawings();
  }, [fetchDrawings]);

  const filtered = filterType ? drawings.filter((d) => d.fileType === filterType) : drawings;
  const existingCodes = [...new Set(drawings.map((d) => d.drawingCode).filter(Boolean))];

  return (
    <div className="space-y-6">
      {preview && <PreviewModal drawing={preview} onClose={() => setPreview(null)} />}

      <PageHeader
        title="Drawings"
        subtitle="CAD files & documents"
        action={
          selectedSite ? (
            <Button onClick={() => setShowUpload(true)} size="md">
              <UploadIcon className="h-4 w-4" />
              Upload
            </Button>
          ) : null
        }
      />

      {/* Site switcher */}
      {sites.length > 1 && (
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-600">Site</p>
          <ChipRow>
            {sites.map((s) => (
              <Chip
                key={s._id}
                active={selectedSite === s._id}
                onClick={() => setSelectedSite(s._id)}
              >
                {s.name}
              </Chip>
            ))}
          </ChipRow>
        </div>
      )}

      {/* Type filter */}
      <ChipRow>
        {["", "pdf", "dwg", "dxf"].map((t) => {
          const count = t ? drawings.filter((d) => d.fileType === t).length : drawings.length;
          return (
            <Chip key={t} active={filterType === t} onClick={() => setFilterType(t)}>
              {t ? `${TYPE_ICON[t]} ${t.toUpperCase()}` : "All"}
              <span className="opacity-70 tabular">{count}</span>
            </Chip>
          );
        })}
      </ChipRow>

      {/* List */}
      {loading ? (
        <SkeletonList count={4} className="h-[92px] rounded-2xl" />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="📐"
          title={drawings.length ? "Nothing matches this filter" : "No drawings yet"}
          hint={
            drawings.length
              ? "Try a different file type."
              : selectedSite
              ? "Upload the first drawing for this site."
              : "You have no sites assigned yet."
          }
          action={
            selectedSite && !drawings.length ? (
              <Button onClick={() => setShowUpload(true)}>
                <UploadIcon className="h-4 w-4" />
                Upload drawing
              </Button>
            ) : null
          }
        />
      ) : (
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((d) => (
            <li key={d._id}>
              <Card className="flex h-full flex-col gap-3 p-4">
                <div className="flex items-start gap-3">
                  <span
                    aria-hidden="true"
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border text-2xl ${
                      TYPE_STYLE[d.fileType] || "border-slate-200 bg-slate-50"
                    }`}
                  >
                    {TYPE_ICON[d.fileType] || "📁"}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="break-anywhere line-clamp-2 text-sm font-semibold text-slate-900">
                      {d.title}
                    </p>
                    <p className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-slate-500">
                      <span className="font-mono">{d.drawingCode}</span>
                      <span>v{d.version}</span>
                      <span>{formatBytes(d.fileSize)}</span>
                    </p>
                  </div>
                </div>

                {/* Actions get their own full-width row — side-by-side stacked
                    buttons were ~28px tall and impossible to hit with a glove. */}
                <div className="mt-auto grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setPreview(d)}
                    className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-300 bg-white text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 active:scale-[0.97]"
                  >
                    <EyeIcon className="h-4 w-4" />
                    View
                  </button>
                  <a
                    href={d.firebaseURL}
                    download={d.fileName}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-300 bg-white text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 active:scale-[0.97]"
                  >
                    <DownloadIcon className="h-4 w-4" />
                    Save
                  </a>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}

      {showUpload && (
        <UploadSheet
          sites={sites}
          adminId={user?.adminId}
          siteId={selectedSite}
          existingCodes={existingCodes}
          onSuccess={() => {
            setShowUpload(false);
            fetchDrawings();
          }}
          onClose={() => setShowUpload(false)}
        />
      )}
    </div>
  );
}
